import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Edit, Trash2, Phone, CalendarClock } from "lucide-react"
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card"
import PropTypes from "prop-types"
import { useState } from "react"
import { DoctorScheduleContent } from "./DoctorSchedule"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

function getInitials(name) {
  const names = name.split(" ")
  const initials = names
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
  return initials.toUpperCase()
}

function DoctorCard({ doctor, onEdit, onDelete, onClick }) {
  return (
    <HoverCard openDelay={200}>
      <HoverCardTrigger asChild>
        <Card
          className="group hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-700 cursor-pointer overflow-hidden"
          onClick={() => onClick(doctor.id)}
        >
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-0 sm:justify-between">
              <div className="flex items-start space-x-4 flex-1">
                <div className="relative flex-shrink-0">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white flex items-center justify-center text-lg sm:text-xl font-bold shadow-lg transform transition-transform group-hover:scale-105">
                    {getInitials(doctor.name)}
                  </div>
                </div>
                <div className="space-y-1 flex-1 pr-2 sm:pr-4">
                  <TooltipProvider delayDuration={300}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <h3 className="text-base sm:text-lg font-semibold tracking-tight hover:text-blue-600 transition-colors line-clamp-2 break-words">
                          {doctor.name}
                        </h3>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{doctor.name}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
              <div className="flex space-x-2 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 border-gray-200 hover:border-blue-500 hover:text-blue-500 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation()
                    onEdit(doctor)
                  }}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 border-gray-200 hover:border-red-500 hover:text-red-500 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation()
                    onDelete(doctor)
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="mt-4 space-y-2">
              <p className="text-blue-600 font-bold text-sm sm:text-md">
                {doctor.designation}
              </p>
              <p className="text-xs sm:text-sm text-gray-500 line-clamp-2">
                {doctor.qualifications}
              </p>
              <p className="text-xs sm:text-sm text-gray-600 line-clamp-1">
                {doctor.location || "No location available"}
              </p>
              <div className="flex items-center text-xs sm:text-sm text-gray-600 hover:text-blue-600 transition-colors">
                <Phone className="h-4 w-4 mr-2 sm:mr-3 text-gray-400" />
                <span className="truncate">
                  {doctor.phone || "No phone number available"}
                </span>
              </div>
              <div className="flex items-center text-xs sm:text-sm text-gray-600">
                <CalendarClock className="h-4 w-4 mr-2 sm:mr-3 text-gray-400" />
                <span className="text-gray-500 truncate">
                  Last modified:{" "}
                  {new Date(doctor.modified_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </HoverCardTrigger>
      {doctor.schedule && (
        <HoverCardContent
          className="w-[280px] sm:w-80 data-[side=bottom]:animate-slideUpAndFade"
          side="bottom"
          align="end"
          sideOffset={5}
          alignOffset={-10}
        >
          <DoctorScheduleContent schedule={doctor.schedule} />
        </HoverCardContent>
      )}
    </HoverCard>
  )
}

DoctorCard.propTypes = {
  doctor: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    designation: PropTypes.string.isRequired,
    qualifications: PropTypes.string.isRequired,
    phone: PropTypes.string.isRequired,
    modified_at: PropTypes.string.isRequired,
    location: PropTypes.string, // Changed from required to optional
    schedule: PropTypes.object,
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
}

export default DoctorCard
